import defaultState from "viewer/default_state";
import type { Action } from "viewer/model/actions/actions";
import { updateKey, updateKey2 } from "viewer/model/helpers/deep_update";
import type { WebknossosState } from "viewer/store";

// Note that this reducer is also part of the store outside of the viewer (see viewer/store.ts).
// Therefore, it must not depend on the viewer's heavier modules (e.g., via the accessors).

function UiReducer(state: WebknossosState, action: Action): WebknossosState {
  switch (action.type) {
    case "RESET_STORE": {
      return {
        ...defaultState,
        activeUser: state.activeUser,
        activeOrganization: state.activeOrganization,
        uiInformation: {
          ...defaultState.uiInformation,
          systemTheme: state.uiInformation.systemTheme,
          storedLayouts: state.uiInformation.storedLayouts,
          navbarHeight: state.uiInformation.navbarHeight,
          isWkInitialized: false,
          isUiReady: false,
        },
      };
    }

    case "SET_DROPZONE_MODAL_VISIBILITY": {
      return updateKey(state, "uiInformation", {
        showDropzoneModal: action.visible,
      });
    }

    case "SET_VERSION_RESTORE_VISIBILITY": {
      return updateKey(state, "uiInformation", {
        showVersionRestore: action.active,
      });
    }
    case "SET_IS_RESTORING_VERSION": {
      return updateKey(state, "uiInformation", {
        isRestoringVersion: action.isRestoring,
      });
    }

    case "SET_STORED_LAYOUTS": {
      const { storedLayouts } = action;
      return updateKey(state, "uiInformation", {
        storedLayouts,
      });
    }

    case "SET_IMPORTING_MESH_STATE": {
      return updateKey(state, "uiInformation", {
        isImportingMesh: action.isImporting,
      });
    }

    case "SET_IS_IN_ANNOTATION_VIEW": {
      return updateKey(state, "uiInformation", {
        isInAnnotationView: action.value,
      });
    }

    case "SET_HAS_ORGANIZATIONS": {
      return updateKey(state, "uiInformation", {
        hasOrganizations: action.value,
      });
    }

    case "SET_BORDER_OPEN_STATUS": {
      return updateKey(state, "uiInformation", {
        borderOpenStatus: action.borderOpenStatus,
      });
    }

    case "SET_SYSTEM_THEME": {
      return updateKey(state, "uiInformation", {
        systemTheme: action.systemTheme,
      });
    }

    case "SET_DOWNLOAD_MODAL_VISIBILITY": {
      return updateKey(state, "uiInformation", { showDownloadModal: action.visible });
    }

    case "SET_PYTHON_MODAL_VISIBILITY": {
      return updateKey(state, "uiInformation", { showPythonClientModal: action.visible });
    }

    case "SET_SHARE_MODAL_VISIBILITY": {
      return updateKey(state, "uiInformation", {
        showShareModal: action.visible,
      });
    }

    case "SET_AI_JOB_DRAWER_STATE": {
      return updateKey(state, "uiInformation", {
        aIJobDrawerState: action.state,
      });
    }

    case "SET_MERGE_MODAL_VISIBILITY": {
      return updateKey(state, "uiInformation", {
        showMergeAnnotationModal: action.visible,
      });
    }

    case "SET_USER_SCRIPTS_MODAL_VISIBILITY": {
      return updateKey(state, "uiInformation", {
        showAddScriptModal: action.visible,
      });
    }

    case "SET_ZARR_LINKS_MODAL_VISIBILITY": {
      return updateKey(state, "uiInformation", {
        showZarrPrivateLinksModal: action.visible,
      });
    }

    case "SET_KEYBOARD_SHORTCUT_CONFIG_MODAL_VISIBILITY": {
      return updateKey(state, "uiInformation", {
        showKeyboardShortcutConfigModal: action.visible,
      });
    }

    case "SET_DUPLICATE_ANNOTATION_MODAL_VISIBILITY": {
      return updateKey(state, "uiInformation", {
        showDuplicateAnnotationModal: action.visible,
      });
    }

    case "SET_CREATE_ANIMATION_MODAL_VISIBILITY": {
      return updateKey(state, "uiInformation", {
        showRenderAnimationModal: action.visible,
      });
    }

    case "SET_IS_WK_INITIALIZED": {
      return updateKey(state, "uiInformation", { isWkInitialized: action.isInitialized });
    }
    case "WK_INITIALIZED": {
      return updateKey(state, "uiInformation", { isWkInitialized: true });
    }
    case "UI_READY": {
      return updateKey(state, "uiInformation", { isUiReady: true });
    }

    case "SET_QUICK_SELECT_STATE": {
      return updateKey(state, "uiInformation", {
        quickSelectState: action.state,
      });
    }

    case "SET_ARE_QUICK_SELECT_SETTINGS_OPEN": {
      return updateKey(state, "uiInformation", {
        areQuickSelectSettingsOpen: action.isOpen,
      });
    }

    case "HIDE_MEASUREMENT_TOOLTIP": {
      return updateKey2(state, "uiInformation", "measurementToolInfo", {
        lastMeasuredPosition: null,
      });
    }

    case "SET_LAST_MEASURED_POSITION": {
      return updateKey2(state, "uiInformation", "measurementToolInfo", {
        lastMeasuredPosition: action.position,
      });
    }
    case "SET_VOXEL_PIPETTE_TOOLTIP_PINNED_POSITION": {
      return updateKey2(state, "uiInformation", "voxelPipetteToolInfo", {
        pinnedPosition: action.position,
      });
    }
    case "SET_IS_MEASURING": {
      return updateKey2(state, "uiInformation", "measurementToolInfo", {
        isMeasuring: action.isMeasuring,
      });
    }
    case "SET_NAVBAR_HEIGHT": {
      return updateKey(state, "uiInformation", {
        navbarHeight: action.navbarHeight,
      });
    }

    case "SHOW_CONTEXT_MENU": {
      return updateKey2(state, "uiInformation", "contextInfo", {
        contextMenuPosition: action.contextMenuPosition,
        clickedNodeId: action.clickedNodeId,
        clickedBoundingBoxId: action.clickedBoundingBoxId,
        globalPosition: action.globalPosition,
        viewport: action.viewport,
        meshId: action.meshId,
        meshIntersectionPosition: action.meshIntersectionPosition,
        unmappedSegmentId: action.unmappedSegmentId,
      });
    }

    case "HIDE_CONTEXT_MENU": {
      return updateKey2(state, "uiInformation", "contextInfo", {
        contextMenuPosition: null,
        clickedNodeId: null,
        clickedBoundingBoxId: null,
        globalPosition: null,
        viewport: null,
        meshId: null,
        meshIntersectionPosition: null,
        unmappedSegmentId: null,
      });
    }
    case "SET_ACTIVE_USER_BOUNDING_BOX_ID": {
      return updateKey(state, "uiInformation", {
        activeUserBoundingBoxId: action.id,
      });
    }

    case "SET_GLOBAL_PROGRESS": {
      return updateKey(state, "uiInformation", {
        globalProgress: action.value,
      });
    }

    default:
      return state;
  }
}

export default UiReducer;
